// Outbox module for offline-first sync
import { and, asc, desc, eq, inArray, sql } from 'drizzle-orm';
import type { BaseSQLiteDatabase } from 'drizzle-orm/sqlite-core';
import { outbox } from './schema';
import { v7 as uuidv7 } from 'uuid';

// 'sync' cobre os testes (better-sqlite3) e 'async' cobre o app real
// (expo-sqlite via drizzle-orm/sqlite-proxy — ver src/db/client.ts). Todo
// código deste módulo e de src/sync já usa `await` em cada chamada, então
// funciona nos dois modos sem alteração.
export type AppDatabase = BaseSQLiteDatabase<'sync' | 'async', any>;
export type OutboxEntry = typeof outbox.$inferSelect;

export async function enqueue(
  db: AppDatabase,
  tableName: string,
  recordId: string,
  operation: 'INSERT' | 'UPDATE' | 'DELETE',
  payload: any
): Promise<string> {
  const id = uuidv7();
  await db.insert(outbox).values({
    id,
    tableName,
    recordId,
    operation,
    payload: JSON.stringify(payload),
    attemptCount: 0,
    createdAt: new Date(),
  });
  return id;
}

export async function markProcessed(db: AppDatabase, outboxId: string): Promise<void> {
  await db.update(outbox).set({ processedAt: new Date(), status: 'processed' }).where(eq(outbox.id, outboxId));
}

/**
 * Quarentena (Etapa 15 — spec S05 §Estados na UI): usado quando `pushAll`
 * recebe de volta um erro NÃO recuperável do Supabase (ex.: 4xx de
 * constraint/RLS) — nunca para erro de rede/transporte, esses continuam só
 * incrementando `attemptCount` via `incrementAttempt`. Tira o item da fila
 * normal (`getPending` filtra por status='pending') sem apagar o registro:
 * o erro fica visível em `lastError` em vez de a escrita sumir em silêncio.
 */
export async function markQuarantined(db: AppDatabase, outboxId: string, errorMessage: string): Promise<void> {
  await db
    .update(outbox)
    .set({ status: 'quarantined', lastError: errorMessage, lastAttemptAt: new Date() })
    .where(eq(outbox.id, outboxId));
}

export async function getPending(db: AppDatabase): Promise<OutboxEntry[]> {
  // status='pending' (não mais `isNull(processedAt)`): itens quarentenados
  // (Etapa 15) têm processedAt permanentemente nulo mas não devem voltar a
  // ser tentados — sem essa filtragem, o loop de drenagem de `pushAll`
  // nunca convergiria (o mesmo item quarentenado reapareceria a cada
  // iteração).
  //
  // Ordena por createdAt — mais legível/intencional que ordenar por `id` —
  // com `id` como desempate: createdAt tem resolução de milissegundo e duas
  // chamadas de enqueue em sequência rápida podem colidir no mesmo ms; `id`
  // é UUIDv7 (ordenável lexicograficamente por tempo de geração — regra
  // invíolável #6 do projeto), então o desempate ainda preserva
  // parent-antes-de-filhas na prática, sem depender da ordem física de
  // armazenamento do SQLite.
  const result = await db
    .select()
    .from(outbox)
    .where(eq(outbox.status, 'pending'))
    .orderBy(asc(outbox.createdAt), asc(outbox.id))
    .limit(10);
  return result;
}

export async function incrementAttempt(db: AppDatabase, outboxId: string): Promise<void> {
  await db
    .update(outbox)
    .set({
      attemptCount: sql`attempt_count + 1`,
      lastAttemptAt: new Date(),
    })
    .where(eq(outbox.id, outboxId));
}

/**
 * Etapa 15 (S05 §Deleção) — usado pelo purge local/de fotos (src/sync/purge.ts)
 * para decidir se é seguro apagar fisicamente uma linha: existe algum item
 * de outbox NÃO resolvido (pending ou quarantined) para este registro, em
 * qualquer operação? Se sim, a linha NUNCA pode ser purgada — purgar antes
 * do soft delete estar de fato confirmado no servidor faz o item da outbox
 * apontar para um registro local inexistente, e a exclusão nunca chega ao
 * servidor. Verifica TODOS os itens do registro (não só o mais recente):
 * pode haver mais de um item processado ao longo do tempo (edições antes da
 * exclusão), mas basta UM pending/quarantined para bloquear o purge.
 */
export async function hasUnresolvedOutboxItems(
  db: AppDatabase,
  tableName: string,
  recordId: string
): Promise<boolean> {
  const row = await db
    .select({ id: outbox.id })
    .from(outbox)
    .where(
      and(
        eq(outbox.tableName, tableName),
        eq(outbox.recordId, recordId),
        inArray(outbox.status, ['pending', 'quarantined'])
      )
    )
    .get();
  return !!row;
}

/** Itens em quarentena (Etapa 15 — tela de debug em Perfil), mais recentes
 * primeiro. Só visibilidade — nenhuma ação de retry nesta etapa. */
export async function getQuarantined(db: AppDatabase): Promise<OutboxEntry[]> {
  return db.select().from(outbox).where(eq(outbox.status, 'quarantined')).orderBy(desc(outbox.lastAttemptAt));
}
